<template>
  <div
    id="repair-shops"
    :data-test-selected-repair-shop-postal-code="selectedRepairShop.PostalCode"
    :data-test-selected-repair-shop-postal-repair-shop-id="selectedRepairShop.RepairShopId"
  >
    <!-- Repair shops search input -->
    <div class="control">
      <label for="" class="text-question">
        {{ contentModel.LabelAndTooltipRepairShopLocationPostalCode }}
      </label>
      <Field
        v-slot="{ field, meta }"
        name="repair-shop-search"
        v-model="selectedRepairShop.PostalCode"
        :rules="{ required: true, numeric: true }"
        :label="contentModel.ErrorMessageRepairShopLocationPostalCode"
      >
        <div class="row">
          <div class="small-6 columns">
            <input
              v-bind="field"
              id="repair-shop-search"
              type="search"
              maxlength="4"
              @keyup.enter="searchRepairShops"
            />
            <ValidationError name="repair-shop-search" />
          </div>
          <div class="medium-3 small-6 columns">
            <button 
              :disabled="meta.errors.length > 0"
              class="button"
              @click="searchRepairShops"
            >
              {{ contentModel.LabelRepairShopSearchButton }}
              <div v-show="searchingRepairShops" class="spinner spinner-button" />
            </button>
          </div>
        </div>
      </Field>
    </div>

    <!-- Repair shops search results -->
    <div class="repair-shops-results" data-test-repair-shops-results>
      <!-- mobile -->
      <div v-if="!mediaQueries.isLargeUp" class="hide-for-large">
        <p v-show="searchingRepairShops">
          {{ contentModel.RepairShopLoadingText }}
        </p>
        <p v-show="repairShopsList.length === 0 && !searchingRepairShops">
          {{ contentModel.RepairShopNoResultsFoundText }}
        </p>

        <ul v-show="!searchingRepairShops" class="no-bullet table-list--mobile">
          <li v-for="(repairShop, index) in repairShopsList" :key="index">
            <div>
              <Field
                v-slot="{ field }"
                v-model="selectedRepairShop.RepairShopId"
                :rules="{ required: true }"
                name="repair-shop"
                type="radio"
              >
                <input
                  v-bind="field"
                  :id="'repair-shop-' + index"
                  :value="repairShop.RepairShopId"
                  type="radio"
                  :class="{ invalid: errors['repair-shop'] !== undefined }"
                  @change="onChange"
                  :checked="selectedRepairShop.RepairShopId === repairShop.RepairShopId"
                />
              </Field>
              <label :for="'repair-shop-' + index" />
            </div>
            <div>
              <p>
                <strong>{{ contentModel.Column1 }}:</strong> {{ repairShop.Name }}
              </p>
              <p>
                <strong>{{ contentModel.Column2 }}:</strong> {{ repairShop.Address }}
              </p>
              <p>
                <strong>{{ contentModel.Column3 }}:</strong> {{ repairShop.Expertise }}
              </p>
              <p>
                <strong>{{ contentModel.Column4 }}:</strong> {{ repairShop.Wait }}
              </p>
            </div>
          </li>
        </ul>
      </div>

      <!-- desktop -->
      <table v-if="mediaQueries.isLargeUp" class="stack show-for-large claims__car__table">
        <thead>
          <tr>
            <td>&emsp;&emsp;</td>
            <td>{{ contentModel.Column1 }}</td>
            <td>{{ contentModel.Column2 }}</td>
            <td>{{ contentModel.Column3 }}</td>
            <td>{{ contentModel.Column4 }}</td>
          </tr>
        </thead>
        <tbody v-show="searchingRepairShops">
          <tr>
            <td colspan="4">
              {{ contentModel.RepairShopLoadingText }}
            </td>
          </tr>
        </tbody>
        <tbody v-if="repairShopsList.length === 0 && !searchingRepairShops">
          <tr>
            <td colspan="4">
              {{ contentModel.RepairShopNoResultsFoundText }}
            </td>
          </tr>
        </tbody>

        <tbody v-show="!searchingRepairShops">
          <tr v-for="(repairShop, index) in repairShopsList" :key="index">
            <td>
              <Field
                v-slot="{ field }"
                v-model="selectedRepairShop.RepairShopId"
                :rules="{ required: true }"
                name="repair-shop"
                type="radio"
              >
                <input
                  v-bind="field"
                  :id="'repair-shop-' + index"
                  :value="repairShop.RepairShopId"
                  type="radio"
                  :class="{ invalid: errors['repair-shop'] !== undefined }"
                  @change="onChange"
                  :checked="selectedRepairShop.RepairShopId === repairShop.RepairShopId"
                />
              </Field>
              <label :for="'repair-shop-' + index" />
            </td>
            <td>{{ repairShop.Name }}</td>
            <td>{{ repairShop.Address }}</td>
            <td>{{ repairShop.Expertise }}</td>
            <td>{{ repairShop.Wait }}</td>
          </tr>
        </tbody>
      </table>

      <ValidationError
        :overrides="{ required: contentModel.ErrorMessageRepairShop }"
        :errors="errors"
        name="repair-shop"
      />

      <div
        class="info-box info-box-align-left is-shown push-down"
        v-html="contentModel.RepairShopInfoBoxText"
      />
    </div>
  </div>
</template>

<script>
import { hasProperty } from '@/common/Util/utility';
import BaseMixin from '@/components/Feature/BaseMixin.vue';
import Utility from '@/common/UtilityFunctions.js';
import ReportClaimServicesMixin from '@/components/Feature/Claims/flows/ReportClaimServicesMixin.vue';

export default {
  name: 'RepairShops',
  mixins: [BaseMixin, ReportClaimServicesMixin],
  props: {
    active: {
      type: Boolean,
      default: false
    },
    contentModel: {
      type: Object,
      default() {
        return {};
      }
    },
    questions: {
      type: Array,
      default() {
        return [];
      }
    },
    trackingVm: {
      type: Object,
      default() {
        return {};
      }
    },
    errors: {
      type: Object,
      default() {
        return null;
      }
    },
    modelValue: {
      type: Object,
      default() {
        return {};
      }
    },
    minorDamage: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      repairShopsList: [],
      searchingRepairShops: false,
      selectedRepairShop: this.modelValue
    };
  },
  watch: {
    'selectedRepairShop.RepairShopId': function () {
      this.$emit('update:modelValue', this.selectedRepairShop);
    },
    repairShopId(val) {
      this.selectedRepairShop.RepairShopId = val;
    },
    minorDamage: function () {
      this.getRepairShops();
    }
  },
  mounted() {
    // this.selectedRepairShop = this.modelValue;
    this.searchRepairShops();
  },
  methods: {
    onChange(event) {
      var repairShopId = event.target.value;
      let foundShop = this.repairShopsList.find((p) => p.RepairShopId === repairShopId);
      if (foundShop !== undefined) {
        this.selectedRepairShop.IsParkingDamage = foundShop.IsParkingDamage;
      }
    },
    searchRepairShops(errors) {
      if (this.errors[this.name] !== undefined) {
        return;
      }

      this.searchingRepairShops = true;
      this.getRepairShops();
    },
    getRepairShops() {
      const vm = this;
      vm.repairShopsList = [];
      return Utility.post(
        Utility.constants().API.get_repair_shops,
        {
          ManufactureId: this.selectedRepairShop.ManufactureId,
          NearestBest: 1,
          LicensePlate: this.selectedRepairShop.LicensePlate,
          PostalCode: parseInt(this.selectedRepairShop.PostalCode),
          ModelYear: parseInt(this.selectedRepairShop.ModelYear),
          RepairShopId: this.repairShopId,
          IsParkingDamage: this.isParkingDamage(),
          QuestionParkingAnswerId: this.selectedRepairShop.QuestionParkingAnswerId,
          QuestionParkingLineId: this.selectedRepairShop.QuestionParkingLineId,
          IsMinorDamage: this.minorDamage
        },
        (response) => {
          if (hasProperty(response.data, 'Data')) {
            // RepairShop does not work on preprod so the responce might be empty this if is there to ensure the call fails gracefully frontend wise
            vm.repairShopsList = response.data.Data.RepairShopListResult.RepairShopList;
          } else {
            vm.repairShopsList = [];
          }
          if (!response.data.IsSuccess) {
            this.dispatchEventObjWithApiError(this.trackingVm, 'get_repair_shops: ' + response?.data?.FrontEndTitleMessage);
          }
          vm.searchingRepairShops = false;
        },
        (error) => {
          this.dispatchEventObjWithApiError(this.trackingVm, 'get_repair_shops: ' + error?.message);
          vm.searchingRepairShops = false;
        },
        vm
      );
    },
    isParkingDamage() {
      return this.questions.some(
        (questions) =>
          questions.LineId === this.selectedRepairShop.QuestionParkingLineId &&
          questions.ChosenAnswer === this.selectedRepairShop.QuestionParkingAnswerId
      );
    }
  }
};
</script>
